class OrbitCarousel extends HTMLElement {
    constructor() {
        super();
        this.track = this.querySelector('.carousel-track');
        this.brands = [];
        this.opacity = parseInt(this.dataset.opacity)/100;
        this.currentIndex = 0;
        this.resizeTimer = null;

        // Bind methods to maintain 'this' context
        this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
        this.loadData();
        if (this.brands.length > 0) {
            this.currentIndex = Math.floor(this.brands.length / 2);
            this.renderItems();
            this.updateCarousel();
            window.addEventListener('resize', this.handleResize);
        }
    }

    disconnectedCallback() {
        window.removeEventListener('resize', this.handleResize);
    }

    loadData() {
        // Extract configuration from the DOM (Standard Shopify practice)
        const dataScript = this.querySelector('[data-orbit-data]');
        if (dataScript) {
            try {
                this.brands = JSON.parse(dataScript.textContent);
            } catch (error) {
                console.error('OrbitCarousel: Failed to parse brand data', error);
            }
        }
    }

    renderItems() {
        if (!this.track) return;
        this.track.innerHTML = ''; // Clear existing content

        this.brands.forEach((brand, index) => {
            const item = document.createElement('div');
            item.className = 'carousel-item absolute top-1/2 left-1/2 w-[220px] md:w-[320px] aspect-[4/5] origin-center cursor-pointer group';
            item.dataset.index = index;

            item.innerHTML = `
                <!-- Image Card -->
                <div class="relative w-full h-full rounded-lg overflow-hidden shadow-2xl bg-slate-200">
                    <img src="${brand.img}" alt="${brand.name}" class="w-full h-full object-cover select-none pointer-events-none" />
                    <div class="absolute inset-0 bg-black/10 group-hover:bg-black/0 transition-colors duration-500 img-overlay pointer-events-none"></div>
                </div>

                <!-- Text & CTA Below Image -->
                <div class="flex flex-col items-center mt-6 transition-all duration-500 item-text">
                    <h4 class="text-xl font-bold mb-1">${brand.name}</h4>
                    <p class="text-slate-500 text-sm font-light mb-4">${brand.tagline}</p>

                    <!-- CTA Button (Fade up when centered) -->
                    <div class="cta-container transition-all duration-500 ease-out translate-y-4 opacity-0 pointer-events-none">
                        <a href="${brand.url}" class="bg-slate-900 text-white font-bold uppercase tracking-widest text-[10px] py-3 px-8 rounded-full flex items-center hover:scale-105 hover:bg-slate-800 transition-all pointer-events-auto">
                            Explore ${brand.name}
                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="ml-2"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
                        </a>
                    </div>
                </div>
            `;

            // Add click event listener to the item
            item.addEventListener('click', () => this.handleItemClick(index));
            this.track.appendChild(item);
        });
    }

    handleItemClick(index) {
        // Outer item clicked: Rotate carousel
        if (index !== this.currentIndex) {
            this.currentIndex = index;
            this.updateCarousel();
        }
    }

    updateCarousel() {
        const items = this.querySelectorAll('.carousel-item');
        const total = items.length;
        const isMobile = window.innerWidth < 768;
        const visibleSideCount = isMobile ? 1 : 2; // Mobile: 3 total visible, Desktop: 5 total visible

        items.forEach((item, index) => {
            // Calculate distance from center (with wrapping logic)
            let offset = index - this.currentIndex;
            if (offset > total / 2) offset -= total;
            if (offset < -total / 2) offset += total;

            const absOffset = Math.abs(offset);

            if (absOffset > visibleSideCount) {
                // Hide items completely out of view bounds
                item.style.opacity = '0';
                item.style.pointerEvents = 'none';
                item.style.transform = `translate(-50%, -50%) translateX(${Math.sign(offset) * 150}%) scale(0.5)`;
                item.querySelector('.item-text').style.opacity = '0';
                item.querySelector('.cta-container').style.opacity = '0';
            } else {
                // Calculate appearance for visible items
                const isCenter = absOffset === 0;
                const opacity = isCenter ? 1 : Math.max(0, this.opacity - (absOffset * 0.1));
                const scale = isCenter ? 1 : 1 - (absOffset * 0.15);
                const zIndex = 100 - absOffset;

                // Positioning Math
                const xSpace = isMobile ? 80 : 110;
                const translateX = offset * xSpace;
                const curveIntensity = isMobile ? 30 : 45;
                const translateY = Math.pow(absOffset, 2) * curveIntensity;
                const rotateZ = 0;//offset * 2;

                // Apply Styles
                item.style.opacity = opacity;
                item.style.pointerEvents = isCenter ? 'none' : 'auto'; // Disable wrapper click if center, allow inner links
                item.style.zIndex = zIndex;
                item.style.transform = `
                    translate(-50%, -50%)
                    translate(${translateX}%, ${translateY}px)
                    scale(${scale})
                    rotate(${rotateZ}deg)
                `;

                // Handle text & CTA visibility and fade-up
                const text = item.querySelector('.item-text');
                const cta = item.querySelector('.cta-container');

                if (isCenter) {
                    text.style.opacity = '1';
                    text.style.transform = 'translateY(0)';

                    // Delay the CTA fade-up so it happens after the rotation
                    setTimeout(() => {
                        // Double check if it's still the center item before showing
                        if (this.currentIndex === index) {
                            cta.style.opacity = '1';
                            cta.style.transform = 'translateY(0)';
                            cta.style.pointerEvents = 'auto';
                        }
                    }, 250);
                } else {
                    text.style.opacity = '0';
                    text.style.transform = 'translateY(-10px)';

                    cta.style.opacity = '0';
                    cta.style.transform = 'translateY(15px)';
                    cta.style.pointerEvents = 'none';
                }
            }
        });
    }

    handleResize() {
        // Debounce the resize event
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
            this.updateCarousel();
        }, 50);
    }
}

// Register the custom element
if (!customElements.get('orbit-carousel')) {
    customElements.define('orbit-carousel', OrbitCarousel);
}

class InteractiveNews extends HTMLElement {
    connectedCallback() {
        // Only run hover logic on desktop where sticky layout is visible
        if (window.innerWidth < 1024) return;

        this.articles = this.querySelectorAll('.interactive-article');
        this.stickyImgContainer = this.querySelector('.sticky-image-container');
        
        // Select the inner img tag injected by the image_v3 snippet
        this.stickyImg = this.stickyImgContainer ? this.stickyImgContainer.querySelector('img') : null;
        this.stickyPub = this.querySelector('#sticky-pub');
        this.stickyTitle = this.querySelector('#sticky-title');

        if (!this.stickyImg) return;

        this.updateStickyContent = this.updateStickyContent.bind(this);

        this.articles.forEach(article => {
        article.addEventListener('mouseenter', () => {
            // Mark all as inactive (dim)
            this.articles.forEach(a => {
            a.classList.remove('lg:opacity-100');
            a.classList.add('lg:opacity-40');
            });
            
            // Mark current as active (full opacity)
            article.classList.remove('lg:opacity-40');
            article.classList.add('lg:opacity-100');

            // Grab data attributes
            const targetImg = article.getAttribute('data-img');
            const targetPub = article.getAttribute('data-pub');
            const targetTitle = article.getAttribute('data-title');

            // Update left side if the image actually changed
            if(this.stickyImg.src !== targetImg && targetImg) {
            this.updateStickyContent(targetImg, targetPub, targetTitle);
            }
        });
        });
    }

    updateStickyContent(imgUrl, pubText, titleText) {
        // Subtle fade out
        this.stickyImg.style.opacity = '0';
        this.stickyPub.style.opacity = '0';
        this.stickyTitle.style.opacity = '0';

        setTimeout(() => {
        this.stickyImg.src = imgUrl;
        // Clear srcset logic generated by image_v3 so it forces new URL
        this.stickyImg.removeAttribute('srcset');
        this.stickyImg.removeAttribute('data-srcset');

        this.stickyPub.textContent = pubText;
        this.stickyTitle.textContent = titleText;
        
        // Fade back in
        this.stickyImg.style.opacity = '1';
        this.stickyPub.style.opacity = '1';
        this.stickyTitle.style.opacity = '1';
        }, 200); // 200ms matches the smooth CSS transition
    }
}

if (!customElements.get('interactive-news')) {
    customElements.define('interactive-news', InteractiveNews);
}

if (!customElements.get('company-highlights')) {
    class CompanyHighlights extends HTMLElement {
      constructor() {
        super();
        // Minimal Custom Element class ready for future JS enhancements
        // e.g., tracking analytics on hover, handling dynamic equal heights if needed, etc.
      }
      connectedCallback() {
        // Optionally dispatch custom events when section loads
        this.classList.add('highlights-loaded');
      }
    }
    customElements.define('company-highlights', CompanyHighlights);
}

if (!customElements.get('lazy-image')) {
    class LazyImage extends HTMLElement {
    connectedCallback() {
        const img = this.querySelector('img');
        const sources = this.querySelectorAll('source');

        const loadImage = () => {
        if (img.complete) {
            this.handleLoad(img);
        } else {
            img.addEventListener('load', () => this.handleLoad(img), { once: true });
        }

        // Swap data-src/srcset to real attributes
        sources.forEach(s => {
            if (s.dataset.srcset) s.srcset = s.dataset.srcset;
        });
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        if (img.dataset.src) img.src = img.dataset.src;
        };

        // Use IntersectionObserver to start loading when near viewport
        if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
            loadImage();
            observer.disconnect();
            }
        }, { rootMargin: '200px' });
        observer.observe(this);
        } else {
        loadImage();
        }
    }

    handleLoad(img) {
        // Add a tiny timeout to ensure smooth transition
        requestAnimationFrame(() => {
        img.classList.add('loaded');
        });
    }
    }
    customElements.define('lazy-image', LazyImage);
}


/**
 * Adds an observer to initialise a script when an element is scrolled into view.
 * @param {Element} element - Element to observe.
 * @param {Function} callback - Function to call when element is scrolled into view.
 * @param {number} [threshold=500] - Distance from viewport (in pixels) to trigger init.
 */
function initLazyScript(element, callback, threshold = 500) {
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (typeof callback === 'function') {
            callback();
            observer.unobserve(entry.target);
          }
        }
      });
    }, { rootMargin: `0px 0px ${threshold}px 0px` });

    io.observe(element);
  } else {
    callback();
  }
}

window.initLazyScript = initLazyScript;

function renderMobileNav(){
    document.addEventListener('DOMContentLoaded', () => {
        const hamburgerBtn = document.getElementById('hamburger-btn');
        const closeMenuBtn = document.getElementById('close-menu-btn');
        const mobileMenuDrawer = document.getElementById('mobile-menu-drawer');
        const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
        const pageWrapper = document.getElementById('page-wrapper');

        function openMenu() {
            document.body.classList.add('menu-open');
            mobileMenuOverlay.classList.add('open');
            mobileMenuDrawer.classList.add('open');
            pageWrapper.classList.add('open');
        }

        function closeMenu() {
            document.body.classList.remove('menu-open');
            mobileMenuOverlay.classList.remove('open');
            mobileMenuDrawer.classList.remove('open');
            pageWrapper.classList.remove('open');
        }

        if (hamburgerBtn) hamburgerBtn.addEventListener('click', openMenu);
        if (closeMenuBtn) closeMenuBtn.addEventListener('click', closeMenu);
        if (mobileMenuOverlay) mobileMenuOverlay.addEventListener('click', closeMenu);
    });
}

renderMobileNav()